import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Collapse,
  Box,
  Text,
  Divider,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { UploadResponse } from '@common/src/hooks/useUpload';
import ExtractionForm from '@toolkit/src/features/document-extraction/components/ExtractionForm';
import { useInsurancePolicyExtraction } from '@advisory-report/src/features/creation-flow/api/current-insurances/postInsurancepolicyExtraction';
import DocumentExtractionLoading from '@toolkit/src/pages/document-extraction/DocumentExtractionLoading';
import PrefillOfferModalInsurances from '@advisory-report/src/features/creation-flow/components/insurance-policies/offers/PrefillOfferModalInsurances';
import { v4 } from 'uuid';
import { InsurancePolicyType } from '@advisory-report/src/types/AdvisoryReport';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  type: InsurancePolicyType;
}

const AddInsuranceModalExtractionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  type,
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<UploadResponse>();
  const documentId = file?.document_id;
  const filename = encodeURIComponent(file?.filename || '');
  const { data, isLoading } = useInsurancePolicyExtraction({
    documentId,
    filename,
    type,
  });

  /** Temp  */
  const ItemsWithCoverageIds = data?.result?.items?.map((insurance) => ({
    ...insurance,
    documents: file
      ? [...(insurance.documents || []), file]
      : insurance.documents,
    coverages: insurance.coverages?.map((coverage) => ({
      ...coverage,
      id: coverage.id || v4(),
    })),
  }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('INSURANCE-POLICIES_MODAL_ALERT_DOC-EXTRACT_TITLE')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box mb="3">
            {!file && (
              <Text mb="2" textStyle="normalBodyMedium">
                {t('INSURANCE-POLICIES_MODAL_ALERT_DOC-EXTRACT_UPLOAD-LABEL')}
              </Text>
            )}
            <ExtractionForm onUpload={setFile} file={file} showUpload={!file} />
          </Box>
          <Collapse in={!!file}>
            {isLoading ? (
              <DocumentExtractionLoading
                description={t(
                  'INSURANCE-POLICIES_MODAL_ALERT_DOC-EXTRACT_LOADING_DESC',
                )}
              />
            ) : (
              <VStack align="stretch" spacing="3">
                <Divider color="gray.50" />
                <Text textStyle="normalBodyMedium">
                  {t('INSURANCE-POLICIES_MODAL_ALERT_DOC-EXTRACT_FOUND_TITLE')}
                </Text>
                <PrefillOfferModalInsurances
                  data={ItemsWithCoverageIds || []}
                  onClose={onClose}
                />
              </VStack>
            )}
          </Collapse>
        </ModalBody>
        <ModalFooter>
          <Button variant="grayedGhost" onClick={onClose}>
            {t('CLOSE')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddInsuranceModalExtractionModal;
